import { Head, Link, router, usePoll } from '@inertiajs/react'
import { Check, LoaderCircle, Mic2, Pause, Play, TriangleAlert, Users } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { CharacterChip } from '@/components/character-chip'
import { ScriptText } from '@/components/script-text'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { usePlayer } from '@/hooks/use-player'
import { adminCharacters, adminProductions, adminVoiceAuditions } from '@/routes'
import type { Character, Production, Voice } from '@/types'

type AuditionTake = { lineId: number; status: string; error: string | null; audioUrl: string | null }

type Props = {
  production: Production
  character: Character
  lines: { id: number; text: string; cost: number }[]
  defaultSampleIds: number[]
  voices: (Voice & { description?: string; useCase?: string })[]
  voicesError: string | null
  /** Other characters in this production already cast in each voice, by voice id. */
  castElsewhere: Record<string, Pick<Character, 'id' | 'name' | 'color'>[]>
  auditions: Record<string, AuditionTake[]>
}

const ALL = ''

export default function CharacterVoice({
  production,
  character,
  lines,
  defaultSampleIds,
  voices,
  voicesError,
  castElsewhere,
  auditions,
}: Props) {
  const ids = { productionId: production.slug, characterId: character.id }
  const [sample, setSample] = useState<number[]>(defaultSampleIds)
  const [filters, setFilters] = useState({ gender: ALL, age: ALL, accent: ALL, query: '', hideTaken: false })
  const player = usePlayer<string>()

  const sampleCost = lines.filter((l) => sample.includes(l.id)).reduce((n, l) => n + l.cost, 0)
  const generating = Object.values(auditions).some((takes) =>
    takes.some((t) => t.status === 'generating' || t.status === 'pending' || t.status === 'processing'),
  )

  // Refresh audition status while clips are being generated.
  const poll = usePoll(2000, { only: ['auditions'] }, { autoStart: false })
  useEffect(() => {
    generating ? poll.start() : poll.stop()
  }, [generating, poll])

  const options = (key: 'gender' | 'age' | 'accent') =>
    [...new Set(voices.flatMap((v) => (v[key] ? [v[key]] : [])))].sort()

  // Auditioned and current voices first, then the rest, filtered.
  const shown = useMemo(() => {
    const q = filters.query.trim().toLowerCase()
    return voices
      .filter(
        (v) =>
          v.id === character.voiceId ||
          auditions[v.id] ||
          ((!filters.gender || v.gender === filters.gender) &&
            (!filters.age || v.age === filters.age) &&
            (!filters.accent || v.accent === filters.accent) &&
            !(filters.hideTaken && castElsewhere[v.id]) &&
            (!q || `${v.name} ${v.description ?? ''} ${v.useCase ?? ''}`.toLowerCase().includes(q))),
      )
      .sort((a, b) => rank(b) - rank(a))

    function rank(v: Voice) {
      return (v.id === character.voiceId ? 2 : 0) + (auditions[v.id] ? 1 : 0)
    }
  }, [voices, filters, auditions, castElsewhere, character.voiceId])

  const audition = (voiceId: string) =>
    router.visit(adminVoiceAuditions.create(ids), {
      data: { voice_id: voiceId, line_ids: sample },
      preserveScroll: true,
    })

  const cast = (voice: Voice) => {
    const others = castElsewhere[voice.id]
    if (
      others &&
      !window.confirm(
        `${voice.name.split(' - ')[0]} already voices ${others.map((c) => c.name).join(', ')}. Cast it for ${character.name} too?`,
      )
    )
      return
    router.visit(adminCharacters.update({ productionId: production.slug, id: character.id }), {
      data: { character: { voice_id: voice.id } },
      preserveScroll: true,
    })
  }

  return (
    <div className="grid gap-6">
      <Head title={`Voice · ${character.name}`} />
      <div>
        <Link
          href={adminProductions.show(production.slug, { anchor: 'cast' })}
          className="text-sm text-muted-foreground hover:underline"
        >
          ← {production.title}
        </Link>
        <h1 className="flex items-center gap-3 text-2xl font-semibold">
          Voice for <CharacterChip character={character} />
        </h1>
        <p className="text-muted-foreground">
          Preview stock samples for free, audition a few voices on {character.name}’s own lines, then cast one. The
          winning audition clips become {character.name}’s audio — nothing is generated twice.
        </p>
      </div>

      {voicesError && (
        <Alert variant="destructive">
          <TriangleAlert />
          <AlertDescription>{voicesError}</AlertDescription>
        </Alert>
      )}

      <div className="grid gap-6 lg:grid-cols-[22rem_1fr]">
        <Card className="self-start">
          <CardHeader>
            <CardTitle>Audition lines</CardTitle>
            <CardDescription>
              {sample.length} lines · ~{sampleCost.toLocaleString()} credits per voice
            </CardDescription>
          </CardHeader>
          <CardContent className="grid max-h-[32rem] gap-2 overflow-y-auto">
            {lines.length === 0 && <p className="text-sm text-muted-foreground">{character.name} has no lines yet.</p>}
            {lines.map((line) => (
              // biome-ignore lint/a11y/noLabelWithoutControl: Checkbox renders the control inside the label
              <label key={line.id} className="flex cursor-pointer items-start gap-2 text-sm">
                <Checkbox
                  className="mt-0.5"
                  checked={sample.includes(line.id)}
                  onCheckedChange={(on) => setSample((s) => (on ? [...s, line.id] : s.filter((id) => id !== line.id)))}
                />
                <span className="line-clamp-3">
                  <ScriptText text={line.text} />
                </span>
              </label>
            ))}
          </CardContent>
        </Card>

        <div className="grid content-start gap-4">
          <div className="flex flex-wrap gap-2">
            <Input
              className="w-48"
              placeholder="Search voices"
              value={filters.query}
              onChange={(e) => setFilters((f) => ({ ...f, query: e.target.value }))}
            />
            {(['gender', 'age', 'accent'] as const).map((key) => (
              <select
                key={key}
                className="h-9 rounded-md border bg-background py-0 pl-2 pr-8 text-sm capitalize"
                value={filters[key]}
                onChange={(e) => setFilters((f) => ({ ...f, [key]: e.target.value }))}
              >
                <option value={ALL}>Any {key}</option>
                {options(key).map((o) => (
                  <option key={o} value={o}>
                    {o.replace('_', ' ')}
                  </option>
                ))}
              </select>
            ))}
            {Object.keys(castElsewhere).length > 0 && (
              // biome-ignore lint/a11y/noLabelWithoutControl: Checkbox renders the control inside the label
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  checked={filters.hideTaken}
                  onCheckedChange={(on) => setFilters((f) => ({ ...f, hideTaken: on === true }))}
                />
                Hide voices cast as other parts
              </label>
            )}
          </div>

          {shown.map((voice) => {
            const takes = auditions[voice.id] ?? []
            const isCurrent = voice.id === character.voiceId
            const others = castElsewhere[voice.id] ?? []
            const [shortName, ...rest] = voice.name.split(' - ')
            return (
              <Card key={voice.id} className={isCurrent ? 'border-primary' : undefined}>
                <CardContent className="grid gap-3">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="grid gap-0.5">
                      <div className="flex items-center gap-2 font-semibold">
                        {shortName}
                        {isCurrent && (
                          <Badge>
                            <Check /> Cast
                          </Badge>
                        )}
                        {others.length > 0 && (
                          <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-normal text-amber-700 dark:text-amber-500">
                            <Users className="size-3.5" /> {isCurrent ? 'Also cast as' : 'Cast as'}
                            {others.map((c) => (
                              <Link
                                key={c.id}
                                href={adminVoiceAuditions.show({ productionId: production.slug, characterId: c.id })}
                                className="hover:underline"
                                title={`${c.name}’s voice`}
                              >
                                <CharacterChip character={c} />
                              </Link>
                            ))}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-muted-foreground">
                        {[
                          rest.join(' - ') || voice.description,
                          voice.gender,
                          voice.age?.replace('_', ' '),
                          voice.accent,
                        ]
                          .filter(Boolean)
                          .join(' · ')}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {voice.previewUrl && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => player.toggle(`preview-${voice.id}`, voice.previewUrl as string)}
                        >
                          {player.playing === `preview-${voice.id}` ? <Pause /> : <Play />} Stock sample
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={sample.length === 0}
                        onClick={() => audition(voice.id)}
                      >
                        <Mic2 /> {takes.length ? 'Audition again' : 'Audition'} · ~{sampleCost}
                      </Button>
                      {!isCurrent && (
                        <Button size="sm" onClick={() => cast(voice)}>
                          Cast {shortName}
                        </Button>
                      )}
                    </div>
                  </div>

                  {takes.length > 0 && (
                    <div className="grid gap-1.5 border-t pt-3">
                      {takes.map((take) => {
                        const line = lines.find((l) => l.id === take.lineId)
                        const key = `take-${voice.id}-${take.lineId}`
                        return (
                          <div key={key} className="flex items-start gap-2 text-sm">
                            {take.audioUrl ? (
                              <Button
                                variant="secondary"
                                size="icon"
                                className="size-7 shrink-0"
                                aria-label="Play"
                                onClick={() => player.toggle(key, take.audioUrl as string)}
                              >
                                {player.playing === key ? <Pause /> : <Play />}
                              </Button>
                            ) : take.status === 'failed' ? (
                              <TriangleAlert className="mt-1 size-4 shrink-0 text-destructive" />
                            ) : (
                              <LoaderCircle className="mt-1 size-4 shrink-0 animate-spin text-muted-foreground" />
                            )}
                            <span className="pt-0.5 text-muted-foreground">
                              {take.error ? take.error : line ? <ScriptText text={line.text} /> : 'Line removed'}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      </div>
    </div>
  )
}
